/* eslint-disable max-statements */
/* eslint-disable @typescript-eslint/restrict-plus-operands */
import {useState, useEffect} from 'react';
import * as store from '@/store';
import sdk from '@/sdk';
import toast, {ToastType} from '@/components/Toast';
import {WizKnownError} from '@/sdk/common/error';
import {clickSelectBatchDoc, feLogger} from '@/buried';
import CustomCheckbox, {MAX_COUNT} from '../components/CustomCheckbox';

let nodeCountMap: Record<string, number> = {};

// 计算选中数量 - demo期间忽略代码美观度 - 可能会根据pm策略调整
async function processCheckedNodes(halfCheckedKeys: string[], checkedKeys:string[], checkedNodes: any[]) {
    const leafNodes = [];
    const parentNodes = [];
    const fetchNeeded: string[] = [];
    const filterCheckedKeys: string[] = []; // 全选节点列表【过滤父节点被全选的节点，用于后端传参】
    const repository = store.getCurRepository();

    // 拆分leaf和parent节点
    for (const node of checkedNodes) {
        if (!checkedKeys.includes(node.parentGuid)) { // 父节点被全选的不需要处理
            filterCheckedKeys.push(node.docGuid);

            if (node.childCount === 0) {
                leafNodes.push(node);
            } else {
                parentNodes.push(node);
                if (!(node.docGuid in nodeCountMap)) {
                    fetchNeeded.push(node.docGuid);
                }
            }
        }
    }

    // 批量请求节点数量
    const fetchResults = await Promise.all(
        fetchNeeded.map(async (guid) => {
            const ksServer = await sdk.getKsServer();
            const count = await ksServer.getTreeChildCount({
                repositoryGuid: repository?.repositoryGuid as string,
                parentGuid: guid,
                capacity: MAX_COUNT,
            });
            return {guid, count};
        })
    );

    // 缓存数据
    for (const {guid, count} of fetchResults) {
        nodeCountMap[guid] = count;
    }

    // 父节点总数
    const parentCount = parentNodes.reduce((sum, node) => {
        return sum + (nodeCountMap[node.docGuid] ?? 0);
    }, 0);
    // 叶子节点总数
    const leafCount = leafNodes.length;
    // 半选总数
    const halfCheckedCount = halfCheckedKeys.length;

    return {
        parentCount,
        leafCount,
        halfCheckedCount,
        totalCount: parentCount + leafCount + halfCheckedCount,
        nodeCountMap,
        filterCheckedKeys,
    };
}

function useOutsideClick(isTreeBatchOperate: boolean) {
    useEffect(() => {
        function handleClickOutside(event: MouseEvent) {
            const target = event.target as HTMLElement;
            // 点击侧边栏和内容区域之外，退出批量操作
            if (isTreeBatchOperate) {
                const selectors = [
                    // header区域
                    '#rlkbHeader',
                    // header特殊处理 - 头像下拉列表
                    '.avatarDropMenu',
                    // header特殊处理 - 新建下拉列表
                    '.headerCreateDropMenu',
                    // 侧边栏-回收站
                    '#sideBar-nav-item-recycleBin',
                    // 侧边栏 - 个人主页
                    // '#sideBar-nav-item-home', // 这个id包含 主页、个人主页、ta的主页、百科导航。不能直接使用
                    // 知识本详情页 - 成员
                    '#bookContentAddMember',
                    // 知识本详情页 - 新建
                    '#bookContentCreatePos',
                    // 回收站 - 恢复文档
                    '.recycle-bin-recovery',
                    // 成员页 - 添加成员
                    '.memberPageAddMember',
                    // 成员页 - 移除成员
                    '.memberPageRemoveMember',
                ];

                const shouldExitBatch = selectors.some((selector) => target?.closest(selector));

                if (shouldExitBatch) {
                    store.setSidebarSelectBatch(false);
                }
            }
        }

        document.addEventListener('click', handleClickOutside, true);
        return () => {
            if (isTreeBatchOperate) {
                store.setSidebarSelectBatch(false);
            }
            document.removeEventListener('click', handleClickOutside, true);
        };
    }, [isTreeBatchOperate]);
}

// 获取当前节点下（含当前节点）选中的节点数量
async function getCheckedCount(curNode: any, isHalfChecked: boolean): Promise<number> {
    const {checkedKeys, halfCheckedKeys} = store.getTreeSelectedObj();
    const children = curNode.children || [];
    // 边界场景处理
    if (children.length === 0) {
        return isHalfChecked ? 1 : 0;
    }

    const checkedNodes = [];
    const halfCheckedNodes = [];
    const unCheckedNodes = []; // 未选中的节点也要计算，可能断层了，底下有选中的节点
    const repository = store.getCurRepository();

    // 用于计算checkedNodes的节点数量
    const checkedLeafNodes = [];
    const checkedParentNodes = [];
    const fetchNeeded: string[] = [];

    // 节点分类
    for (const node of children) {
        if (checkedKeys.includes(node.docGuid)) {
            checkedNodes.push(node);
            if (node.childCount === 0) {
                checkedLeafNodes.push(node);
            } else {
                checkedParentNodes.push(node);
                if (!(node.docGuid in nodeCountMap)) {
                    fetchNeeded.push(node.docGuid);
                }
            }
        } else if (halfCheckedKeys.includes(node.docGuid)) {
            halfCheckedNodes.push(node);
        } else {
            unCheckedNodes.push(node);
        }
    }

    // 1、checkedNodes数量计算
    const fetchResults = await Promise.all(
        fetchNeeded.map(async (guid) => {
            const ksServer = await sdk.getKsServer();
            const count = await ksServer.getTreeChildCount({
                repositoryGuid: repository?.repositoryGuid as string,
                parentGuid: guid,
                capacity: MAX_COUNT,
            });
            return {guid, count};
        })
    );
    for (const {guid, count} of fetchResults) {
        nodeCountMap[guid] = count;
    }

    // 2、halfCheckedNodes数量计算
    const counts = await Promise.all(
        halfCheckedNodes.map(async (node) => {
            const count = await getCheckedCount(node, true);
            return count;
        })
    );

    // 3、unCheckedNodes数量计算
    const unCheckedCounts = await Promise.all(
        unCheckedNodes.map(async (node) => {
            const count = await getCheckedCount(node, false);
            return count;
        })
    );

    // checkedNodes父节点总数
    const parentCount = checkedParentNodes.reduce((sum, node) => {
        return sum + (nodeCountMap[node.docGuid] ?? 0);
    }, 0);
    // checkedNodes叶子节点总数
    const leafCount = checkedLeafNodes.length;
    // halfCheckedNodes总数
    const halfCheckedCount = counts.reduce((sum, count) => sum + count, 0);
    // unCheckedNodes总数
    const unCheckedCount = unCheckedCounts.reduce((sum, count) => sum + count, 0);

    // checkedNodes父节点总数 + checkedNodes叶子节点总数 + halfCheckedNodes总数 + unCheckedNodes总数 + 自身isHalfChecked
    const total = parentCount + leafCount + halfCheckedCount + unCheckedCount + (isHalfChecked ? 1 : 0);
    return total;
}

/**
 * 判断给定节点及其所有子节点是否均为未选中状态
 *
 * @param node 节点对象
 * @param oriCheckedKeys 已选中节点的key数组
 * @param oriHalfCheckedKeys 半选中节点的key数组
 * @param visited 已访问节点的集合，用于防止循环引用导致的无限递归
 * @returns 如果给定节点及其所有子节点均为未选中状态，则返回true；否则返回false
 */
function isAllChildUnChecked(
    node: any,
    oriCheckedKeys: Array<string | number>,
    oriHalfCheckedKeys: Array<string | number>,
    visited = new Set<any>()
): boolean {
    if (!node || visited.has(node)) {
        return true;
    }

    visited.add(node);

    if (node.children) {
        for (const child of node.children) {
            if (oriCheckedKeys.includes(child.key) || oriHalfCheckedKeys.includes(child.key)) {
                return false;
            }
            if (!isAllChildUnChecked(child, oriCheckedKeys, oriHalfCheckedKeys, visited)) {
                return false;
            }
        }
    }

    return true;
}


export const useTreeBatchOperate = () => {
    // const [checkedKeys, setCheckedKeys] = useState([]);
    const isTreeBatchOperate = store.getSidebarSelectBatch();
    const repository = store.getCurRepository();
    const {checkedKeys, halfCheckedKeys} = store.getTreeSelectedObj();

    // 监听，点击外部区域退出批量模式
    useOutsideClick(isTreeBatchOperate);

    // 重置数据
    useEffect(() => {
        // setCheckedKeys([]);
        store.setTreeSelectedObj({
            checkedKeys: [],
            halfCheckedKeys: [],
            filterCheckedKeys: [],
            selectedCount: 0,
        });
        store.setTreeSelectedFull(false);
        nodeCountMap = {};
    }, [repository?.repositoryGuid, isTreeBatchOperate]);

    async function handleCheck(checkedKeys: any, props: any) {
        const {checked, checkedNodes, halfCheckedKeys, node, event, nativeEvent} = props;
        // setCheckedKeys(checkedKeys);
        const lastCount = store.getTreeSelectedObj().selectedCount || 0;
        const result = await processCheckedNodes(halfCheckedKeys, checkedKeys, checkedNodes);
        if (checked && (result.totalCount - lastCount > 0)) {
            feLogger(clickSelectBatchDoc({num: result.totalCount - lastCount}));
        }
        store.setTreeSelectedObj({
            checkedKeys,
            halfCheckedKeys,
            filterCheckedKeys: result.filterCheckedKeys,
            selectedCount: result.totalCount,
        });
        store.setTreeSelectedFull(result.totalCount >= MAX_COUNT);
    }

    // 检测是否允许选中
    // eslint-disable-next-line max-statements
    async function canCheck(node: any) {
        const {docGuid, checked, halfChecked, childCount} = node;
        const {selectedCount, checkedKeys, halfCheckedKeys} = store.getTreeSelectedObj();
        let capacity = MAX_COUNT - selectedCount;

        // 1、底下所有节点都没有选中的半选节点，走的取消选中操作，无需进行数量验证
        if (halfChecked) {
            let result = isAllChildUnChecked(
                node,
                checkedKeys,
                halfCheckedKeys
            );
            // 适配知识库未展开节点
            if (!node.children && node.childCount > 0) {
                result = true;
            }
            if (result) {
                return true;
            }
        }

        // 2、未选中的节点，或者其他场景的半选节点，需要进行数量验证
        if (!checked || halfChecked) {
            // 超量提示
            if (capacity <= 0) {
                toast({
                    content: `已达到最大选择数量${MAX_COUNT}的限制`,
                    duration: 3000,
                    type: ToastType.Error,
                });
                return false;
            }
            // 叶子节点数量为1，无需请求 - 还是得请求，不然无法判断文档是否被删除
            // if (childCount === 0 && capacity > 0) {
            //     return true;
            // }

            // bugfix:如果当前节点下有已经选中的节点，则capacity需要重新校正，加上当前节点下已选择的节点数量
            const halfCheckedCount = await getCheckedCount(node, halfChecked);
            capacity = capacity + halfCheckedCount;

            if (capacity > MAX_COUNT) {
                capacity = MAX_COUNT;
            }

            // 父节点缓存能读取，无需请求
            if (nodeCountMap[docGuid] && (nodeCountMap[docGuid] <= capacity)) {
                return true;
            }

            try {
                // 父节点，无缓存，请求获取数量
                const ksServer = await sdk.getKsServer();
                const count = await ksServer.getTreeChildCount({
                    repositoryGuid: repository?.repositoryGuid as string,
                    parentGuid: docGuid,
                    capacity,
                });
                if (count > 0) {
                    // 缓存数据
                    nodeCountMap[docGuid] = count;
                    return true;
                } else {
                    // 超量提示
                    toast({
                        content: `已达到最大选择数量${MAX_COUNT}的限制`,
                        duration: 3000,
                        type: ToastType.Error,
                    });
                    return false;
                }
            } catch (error) {
                if (error instanceof WizKnownError) {
                    const returnCode = error.toResult().returnCode;
                    if (returnCode && returnCode === 20113) {
                        toast({
                            content: '所选文档已经发生变化，请刷新后重试',
                            type: ToastType.Error,
                        });
                        return false;
                    }
                    if (returnCode && returnCode === 10505) {
                        toast({
                            content: '子文档层级过多超过上限',
                            type: ToastType.Error,
                        });
                        return false;
                    }
                }
                toast({
                    content: '网络请求出错，请稍后重试',
                    type: ToastType.Error,
                });
                return false;
            }
        }
        return true;
    }

    return {
        checkable: isTreeBatchOperate,
        draggable: !isTreeBatchOperate && !window.isTocPage,
        onCheck: handleCheck,
        canCheck,
        CustomCheckbox,
        checkedKeys: {
            checked: checkedKeys,
            halfChecked: halfCheckedKeys,
        },
    };
};
